import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';
import { useAuth } from '../contexts/AuthContext';
import AuthForm from './rsvp/AuthForm';
import RSVPForm from './rsvp/RSVPForm';
import Footer from './Footer';
import Navigation from './Navigation';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: ${colors.primary.lightTan};
  padding: 2rem 0 0 0; /* Remove bottom padding to eliminate gap */
  position: relative;
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-image: url('/images/cheers-2.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    // background: rgba(0, 0, 0, 0.3);
    z-index: 2;
  }
`;

const RSVPOverlay = styled.div`
  position: absolute;
  top: 35%;
  left: 25%;
  transform: translate(-50%, -50%) rotate(-12deg);
  z-index: 5;
  font-family: 'saint-andrews-queen', serif;
  font-size: 8rem;
  color: ${colors.neutral.cream};
  pointer-events: none;
  user-select: none;
  
  @media (max-width: 1200px) {
    font-size: 6rem;
    top: 25%;
    left: 30%;
  }
  
  @media (max-width: 768px) {
    font-size: 4rem;
    top: 30%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-5deg);
  }
  
  @media (max-width: 480px) {
    font-size: 4rem;
    top: 20%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-5deg);
  }
`;


const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 3;
  margin-top: 100vh;
  min-height: 400px;
  margin-bottom: 0; /* Ensure no bottom margin */

  @media (max-width: 768px) {
    min-height: 450px;
  }

  @media (max-width: 480px) {
    min-height: 350px;
  }
`;

const RSVPHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-family: 'GremeryOne', serif;
  font-size: 3.5rem;
  font-weight: 700;
  color: ${colors.neutral.darkOlive};
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.3rem;
  color: ${colors.neutral.darkBrown};
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-family: 'GremeryTwo', cursive;
  color: ${colors.neutral.darkBrown};
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  padding-top: 3rem;
  min-height: 300px;
  
  @media (max-width: 768px) {
    padding-top: 2rem;
    min-height: 300px;
  }
  
  @media (max-width: 480px) {
    padding-top: 1.5rem;
    min-height: 250px;
  }
`;

const HappyCactusImage = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 2rem;
  
  @media (max-width: 480px) {
    width: 150px;
    margin-bottom: 1.5rem;
  }
`;

const MessageText = styled.p`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.5rem;
  color: ${colors.neutral.darkBrown};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const TestButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: ${colors.primary.gold};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-family: 'GremeryTwo', cursive;
  font-size: 0.8rem;
  cursor: pointer;
  z-index: 100;
  opacity: 0.7;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  @media (max-width: 768px) {
    bottom: 10px;
    right: 10px;
    padding: 0.4rem 0.8rem;
    font-size: 0.7rem;
  }
`;

const RSVPPage: React.FC = () => {
  const { isAuthenticated, checkAuth } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check authentication status when the RSVP page loads
  useEffect(() => {
    const checkAuthenticationStatus = async () => {
      if (!authChecked) {
        await checkAuth();
        setAuthChecked(true);
      }
    };

    checkAuthenticationStatus();
  }, [checkAuth, authChecked]);


  const handleClearToken = () => {
    localStorage.removeItem('wedding_auth_token');
    window.location.reload();
  };

  // Show loading if we haven't checked auth yet
  if (!authChecked) {
    return (
      <PageContainer>
        <BackgroundImage />
        <ContentContainer>
          <LoadingMessage>Loading...</LoadingMessage>
        </ContentContainer>
        <Footer backgroundColor={colors.primary.lightTan} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackgroundImage />
      
      <RSVPOverlay>RSV P</RSVPOverlay>
      
      {/* <TestButton onClick={handleClearToken}>
        Clear Token (Test)
      </TestButton>
       */}
      <Navigation 
        textColor={colors.neutral.darkBrown}
        logoPath="/images/agave-full-logo-brn.png"
      />
      
      <ContentContainer>
        {/* Temporary static message - replace with RSVP form later */}
        <MessageContainer>
          <HappyCactusImage 
            src="/images/svgs/happy-cactus.svg" 
            alt="Happy Cactus" 
          />
          <MessageText>RSVP coming soon...</MessageText>
        </MessageContainer>

        {/* Original RSVP form code - commented out for temporary replacement */}
        {/* 
        <RSVPHeader>
          <Title>RSVP</Title>
          <Subtitle>we are excited to celebrate with you</Subtitle>
        </RSVPHeader>

        {!isAuthenticated ? (
          <AuthForm />
        ) : (
          <RSVPForm />
        )}
        */}
      </ContentContainer>
      <Footer backgroundColor={colors.primary.lightTan} />
    </PageContainer>
  );
};

export default RSVPPage; 